"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = CommerceLeadGenNewModalButton;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _core = require("@linaria/core");
var _base = require("@site/pattern-library-utils/base.functions");
var _traffic = require("@site/pattern-library-utils/traffic");
var _merchButton = require("@site/pattern-library-core/merch-button");
var _useIdempotentImpression = _interopRequireDefault(require("@site/pattern-library-utils/hooks/useIdempotentImpression"));
var _useScriptInjector = _interopRequireWildcard(require("@site/pattern-library-utils/hooks/useScriptInjector"));
var _useOpenModalOnLoad = _interopRequireDefault(require("@site/pattern-library-utils/hooks/useOpenModalOnLoad"));
var _text = _interopRequireDefault(require("@ux/text"));
var _box = _interopRequireDefault(require("@ux/box"));
var _spinner = _interopRequireDefault(require("@ux/spinner"));
var _domains = require("@site/pattern-library-utils/domains");
var _cookies = require("@site/pattern-library-utils/cookies");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable import/no-unassigned-import */

const boxStyles = "pl-bq8zwa4";

/** *
 * CommerceLeadGenNewModalButton component
 * @description A button that opens a lead generation modal when clicked. It handles script injection, loading and error states.
 * @param {Object} props - The component props.
 * @param {string} props.campaignName - The name of the campaign for commerce lead gen.
 * @param {string} props.eid - The event ID for tracking.
 * @param {string} props.errorMessage - The error message to display if the script fails to load or lead gen is unavailable.
 * @param {string} props.loadingMessage - The message to display while the script is loading.
 * @param {string} props.productIntent - The product intent for the lead gen form.
 * @param {string} props.scriptUrl - The URL of the script to load for lead gen functionality.
 * @param {string} props.source - The source of the lead gen request, used for tracking.
 * @param {string} props.sourceApp - The source of the lead gen request, used for specific places.
 * @param {boolean} props.modalOpen When set it will automatically open the form
 * modal on page load; unset will require the user to click the button to open it
 * @param {Object} nsMerchButtonProps - Additional props for the NSMerchButton component.
 * @returns {React.ReactElement} The rendered CommerceLeadGenNewModalButton component.
 * @public
 *
 */
function CommerceLeadGenNewModalButton({
  campaignName,
  eid,
  errorMessage,
  loadingMessage,
  productIntent,
  scriptUrl,
  source,
  sourceApp,
  modalOpen = false,
  ...nsMerchButtonProps
}) {
  const SCRIPT_ID = 'commerce-lead-gen-modal-script';
  const COMMERCE_LEAD_GEN_KEY = 'commerceLeadGen';
  const DEFAULT_SOURCE_APP = 'pattern-library';
  const trafficEventProps = {
    source,
    sourceApp: sourceApp || DEFAULT_SOURCE_APP
  };
  (0, _useIdempotentImpression.default)(`${eid}.impression`, trafficEventProps);
  const scriptStatus = (0, _useScriptInjector.default)({
    src: scriptUrl || '',
    id: SCRIPT_ID
  });
  const isScriptLoading = scriptStatus === _useScriptInjector.SCRIPT_STATUS.LOADING;
  const modalError = [_useScriptInjector.SCRIPT_STATUS.ERROR, _useScriptInjector.SCRIPT_STATUS.IDLE].includes(scriptStatus) || scriptStatus === _useScriptInjector.SCRIPT_STATUS.READY && !window?.[COMMERCE_LEAD_GEN_KEY];
  const disableBtnOnError = !errorMessage && modalError;
  if (modalError) {
    (0, _traffic.logEvent)({
      eid: `${eid}.error.impression`,
      type: 'impression',
      properties: trafficEventProps
    });
  }
  const handleOpenModalClick = (isModalOpenCall = false) => {
    (0, _traffic.logEvent)({
      eid: `${eid}.click`,
      type: isModalOpenCall ? 'impression' : 'click',
      properties: trafficEventProps
    });
    try {
      window?.[COMMERCE_LEAD_GEN_KEY].load({
        customerId: (0, _cookies.getCustomerId)(eid, trafficEventProps),
        market: (0, _base.getCookieValue)('market'),
        productIntent,
        rootDomain: (0, _domains.getRootDomain)(),
        source,
        sourceApp: sourceApp || DEFAULT_SOURCE_APP,
        campaignName
      });
    } catch (e) {
      (0, _traffic.logEvent)({
        eid: `${eid}.click.error`,
        type: 'impression',
        properties: trafficEventProps
      });
    }
  };
  (0, _useOpenModalOnLoad.default)({
    modalOpen,
    eid,
    scriptUrl,
    source,
    sourceApp: sourceApp || DEFAULT_SOURCE_APP,
    handleOpenModalClick
  });
  return /*#__PURE__*/_react.default.createElement(_box.default, {
    className: boxStyles,
    id: "commerce-lead-gen-modal-fos-placement",
    blockAlignChildren: "start",
    orientation: "vertical",
    gap: "md",
    stretch: true
  }, /*#__PURE__*/_react.default.createElement(_box.default, {
    orientation: "horizontal",
    gap: "md"
  }, isScriptLoading ? /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_spinner.default, {
    size: "sm",
    "data-testid": "spinner"
  }), /*#__PURE__*/_react.default.createElement(_text.default.span, {
    as: "paragraph",
    size: 0,
    text: loadingMessage
  })) : /*#__PURE__*/_react.default.createElement(_merchButton.NSMerchButton, (0, _extends2.default)({}, nsMerchButtonProps, {
    disabled: disableBtnOnError
    // use an arrow function to avoid explicitly passing the event as the first parameter
    ,
    onClick: () => handleOpenModalClick(),
    "data-cy": "commerce-lead-gen-modal-open-button"
  }))), modalError && !disableBtnOnError && /*#__PURE__*/_react.default.createElement(_text.default.p, {
    emphasis: "critical",
    size: -1
  }, errorMessage));
}
CommerceLeadGenNewModalButton.propTypes = {
  eid: _propTypes.default.string,
  errorMessage: _propTypes.default.string,
  loadingMessage: _propTypes.default.string,
  productIntent: _propTypes.default.string,
  source: _propTypes.default.string,
  sourceApp: _propTypes.default.string,
  campaignName: _propTypes.default.string,
  scriptUrl: _propTypes.default.string,
  modalOpen: _propTypes.default.bool
};